/*
 * r16sd (2026-10-27): the pure half of the Settings Doctor in V3 - it reads what the server sends (DOCTOR-API.md 1 and 2) and decides
 * nothing of its own. Every customer sentence (a hold, a finding, an auto-handled line, a fix label) is the server's, rendered from
 * kb/doctor-rules.json at read time; this file only orders, groups and counts them. The one exception is SD36 (the catalogue marks
 * it `status: ui`): the save answer's blocked[] carries the server's reason for each setting, and the sentence around them is
 * SD36_TOAST below, a copy of kb/doctor-rules.json SD36 texts.toast (lib/doctorView.parity.test.js fails when the two differ). A
 * save answer that carries its own SD36 finding (stage 5) is used instead of the copy.
 */

export const SD36_TOAST = "{n} settings weren't saved: {list}.";

/**
 * Stage 5 (review SF1): the two words V3 puts around a fix, copies of kb/doctor-rules.json features.v1Done / v1DontShow (the labels
 * V1 shows for the same actions; lib/doctorView.parity.test.js fails when they differ from the catalogue).
 */
export const DOCTOR_WORDS = Object.freeze({ done: 'Done:', dontShow: "Don't show again" });

const LEVELS = { red: 3, amber: 2, info: 1 };
const FINDING_LEVELS = { BLOCK: 3, WARN: 2, INFO: 1 };
const KINDS = new Set(['set', 'focus', 'action', 'hint']);
const ACTIONS = new Set(['switchOn', 'switchOnDevice']);
// stage 5: the fixes POST /uiapi/slots/{slot}/doctor/fix applies (DOCTOR-API 11.2: every "set" plus SD58's action)
const APPLY_ACTIONS = new Set(['useAccountEmail']);

const str = (v) => (typeof v === 'string' && v.trim() ? v.trim() : null);
const toMs = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const t = typeof v === 'number' ? v : Date.parse(String(v));
  return Number.isFinite(t) ? t : null;
};
const nums = (a) => (Array.isArray(a) ? a.map((x) => Number(x)).filter((x) => Number.isFinite(x)) : []);
const strs = (a) => (Array.isArray(a) ? a.map(str).filter(Boolean) : []);

/* ------------------------------------------------------------------ shapes */

/** A Fix (DOCTOR-API 1.4), or null. */
export function normFix(f) {
  if (!f || typeof f !== 'object') return null;
  const kind = str(f.kind);
  const label = str(f.label);
  if (!kind || !KINDS.has(kind) || !label) return null;
  const set = f.set && typeof f.set === 'object' && !Array.isArray(f.set) ? { ...f.set } : null;
  const focus = Number.isFinite(Number(f.focus)) && f.focus !== null && f.focus !== '' ? Number(f.focus) : null;
  return { id: str(f.id) || kind, kind, label, set: set && Object.keys(set).length ? set : null, focus };
}

/** A Hold (DOCTOR-API 1.1), or null when it carries no text. */
export function normHold(h) {
  if (!h || typeof h !== 'object') return null;
  const text = str(h.text);
  if (!text) return null;
  const level = LEVELS[h.level] ? h.level : 'amber';
  return {
    rule: str(h.rule) || '', key: str(h.key) || '', cls: str(h.class), level, scope: str(h.scope) || 'slot', text,
    since: toMs(h.since), until: toMs(h.until), settings: nums(h.settings), features: strs(h.features), fix: normFix(h.fix),
  };
}

/** A Finding (DOCTOR-API 1.2), or null. */
export function normFinding(x) {
  if (!x || typeof x !== 'object') return null;
  const text = str(x.text);
  if (!text) return null;
  const level = FINDING_LEVELS[x.level] ? x.level : 'WARN';
  return {
    rule: str(x.rule) || '', key: str(x.key) || '', level, settings: nums(x.settings), text,
    fixes: (Array.isArray(x.fixes) ? x.fixes : []).map(normFix).filter(Boolean), inputHash: str(x.inputHash), dismissed: x.dismissed === true,
  };
}

/** An AutoInfo (DOCTOR-API 1.3), or null. */
export function normAuto(a) {
  if (!a || typeof a !== 'object') return null;
  const text = str(a.text);
  if (!text) return null;
  return { rule: str(a.rule) || '', key: str(a.key) || '', text, fixes: (Array.isArray(a.fixes) ? a.fixes : []).map(normFix).filter(Boolean) };
}

const byLevel = (a, b) => (LEVELS[b.level] || 0) - (LEVELS[a.level] || 0);
const byFindingLevel = (a, b) => (FINDING_LEVELS[b.level] || 0) - (FINDING_LEVELS[a.level] || 0);

/** Holds to show as banners (red / amber; never info), red first, the server's order kept within a level. */
export function bannerHolds(list) {
  return (Array.isArray(list) ? list : []).map(normHold).filter((h) => h && h.level !== 'info')
    .map((h, i) => ({ h, i })).sort((a, b) => byLevel(a.h, b.h) || a.i - b.i).map((x) => x.h);
}

/* ------------------------------------------------------------------ the slot list */

/** A /uiapi/slots row's banners (holds[]: present only when non-empty). */
export function rowHolds(row) {
  return bannerHolds(row && row.holds);
}

/** A row's attention {holds, findings, level} or null (the server leaves it out when there is nothing). */
export function rowAttention(row) {
  const a = row && row.attention;
  if (!a || typeof a !== 'object') return null;
  const holds = Number(a.holds) || 0;
  const findings = Number(a.findings) || 0;
  if (holds <= 0 && findings <= 0) return null;
  return { holds, findings, level: a.level === 'red' ? 'red' : 'amber' };
}

export const needsAttention = (row) => rowAttention(row) !== null;

/** Does any row carry a doctor field? (A server without the doctor sends none.) */
export function anyDoctorData(rows) {
  return (Array.isArray(rows) ? rows : []).some((r) => r && (Array.isArray(r.holds) || (r.attention && typeof r.attention === 'object')));
}

/**
 * The dashboard tile's figures over the account's rows: how many slots need attention, red vs amber, and the reasons (grouped by the
 * hold's rule key; the first hold's own text names the group), most slots first. A slot with findings only counts as "to check".
 */
export function attentionSummary(rows) {
  const list = Array.isArray(rows) ? rows : [];
  let red = 0;
  let amber = 0;
  let findingsOnly = 0;
  const reasons = new Map();
  list.forEach((row) => {
    const a = rowAttention(row);
    if (!a) return;
    if (a.level === 'red') red += 1; else amber += 1;
    const hs = rowHolds(row);
    if (!hs.length) {
      findingsOnly += 1;
      return;
    }
    const seen = new Set();
    hs.forEach((h) => {
      const k = h.key || h.rule;
      if (seen.has(k)) return;
      seen.add(k);
      const r = reasons.get(k) || { key: k, rule: h.rule, text: h.text, level: h.level, count: 0 };
      r.count += 1;
      if (LEVELS[h.level] > LEVELS[r.level]) r.level = h.level;
      reasons.set(k, r);
    });
  });
  const sorted = Array.from(reasons.values()).sort((a, b) => b.count - a.count || (LEVELS[b.level] - LEVELS[a.level]) || a.key.localeCompare(b.key));
  return { total: list.length, slots: red + amber, red, amber, findingsOnly, clear: Math.max(0, list.length - red - amber), reasons: sorted };
}

/* ------------------------------------------------------------------ /uiapi/me */

/** Is the doctor on at the server? Its answers to /uiapi/me carry `doctor` (and `credits`) only then. */
export function doctorAvailable(me) {
  return !!(me && me.doctor && typeof me.doctor === 'object');
}

/** credits {posts, targets, enforced} -> { posts, targets, enforced, postsOut, targetsOut } or null (not sent / not metered). */
export function creditsView(me) {
  const c = me && me.credits;
  if (!c || typeof c !== 'object' || c.enforced !== true) return null;
  const n = (v) => (v === null || v === undefined || v === '' || !Number.isFinite(Number(v)) ? null : Number(v));
  const posts = n(c.posts);
  const targets = n(c.targets);
  if (posts === null && targets === null) return null;
  return { posts, targets, enforced: true, postsOut: posts !== null && posts <= 0, targetsOut: targets !== null && targets <= 0 };
}

/**
 * Review SF7: does the account USE each credit? Posts: a slot needs post credits (doctor.postCreditSlots > 0) or the server holds
 * SD03 for the account; targets: the server holds SD26 (it does so only while target rows wait). A balance of 0 is in the danger
 * tone only when it is used - most accounts sit at 0 on a credit they never spend, and two red zeros on every page say nothing.
 */
export function creditsInUse(me) {
  const d = me && me.doctor && typeof me.doctor === 'object' ? me.doctor : null;
  if (!d) return { posts: false, targets: false };
  const holds = Array.isArray(d.accountHolds) ? d.accountHolds : [];
  const has = (rule, key) => holds.some((h) => h && (h.rule === rule || h.key === key));
  return { posts: Number(d.postCreditSlots) > 0 || has('SD03', 'creditsPosts'), targets: has('SD26', 'creditsTargets') };
}

/** The account banners (doctor.accountHolds), red first. */
export function accountHolds(me) {
  return bannerHolds(me && me.doctor && me.doctor.accountHolds);
}

/* ------------------------------------------------------------------ the doctor panel */

/** GET /uiapi/slots/{slot}/doctor -> the drawer's model. */
export function normPanel(data) {
  const d = data && typeof data === 'object' ? data : {};
  const holds = (Array.isArray(d.holds) ? d.holds : []).map(normHold).filter(Boolean);
  const findings = (Array.isArray(d.findings) ? d.findings : []).map(normFinding).filter(Boolean).sort(byFindingLevel);
  return {
    slot: str(d.slot),
    doctor: d.doctor !== false,
    banners: holds.filter((h) => h.level !== 'info').sort(byLevel),
    infoHolds: holds.filter((h) => h.level === 'info'),
    autoHandled: (Array.isArray(d.autoHandled) ? d.autoHandled : []).map(normAuto).filter(Boolean),
    findings: findings.filter((f) => !f.dismissed),
    dismissedFindings: findings.filter((f) => f.dismissed),
    computedAt: toMs(d.computedAt),
  };
}

/** What a failed panel read means: 'routeMissing' (a server without the doctor), 'slotNotFound', 'failed' (its own words), 'other'. */
export function panelError(err) {
  const res = err && err.response;
  const status = res && typeof res.status === 'number' ? res.status : null;
  const data = res && res.data && typeof res.data === 'object' ? res.data : null;
  const code = data && typeof data.error === 'string' && /^[a-z][a-z0-9_]*$/.test(data.error) ? data.error : null;
  const message = data ? (str(data.message) || null) : null;
  if (code === 'slot_not_found') return { kind: 'slotNotFound', status, message };
  if (code) return { kind: 'failed', status, message };
  if (status === 404 || status === 405) return { kind: 'routeMissing', status, message: null };
  return { kind: 'other', status, message: null };
}

/* ------------------------------------------------------------------ fixes */

/**
 * How a fix is offered on the page (DOCTOR-API 1.4):
 *   action switchOn / switchOnDevice -> a button (the existing pause PUTs);
 *   set, and action useAccountEmail -> with `opts.apply` (stage 5, the fixApply switch, no unsaved edits on the page) a one-click
 *     button: POST .../doctor/fix {rule, fix}, the server derives the values (mode 'apply'); without it a set opens the setting
 *     (mode 'link') and useAccountEmail is plain text (review SF2: never a link that only opens 1229);
 *   focus -> a link that opens the setting;
 *   hint, or an action with no endpoint -> the label as plain text.
 * focusId: the setting the link opens (the fix's focus, the first id it sets, else the finding's first setting).
 */
export function fixTarget(fix, settings, opts) {
  const f = fix && fix.kind ? fix : normFix(fix);
  if (!f) return { mode: 'none' };
  if (f.kind === 'action' && ACTIONS.has(f.id)) return { mode: 'button', action: f.id, label: f.label };
  if (f.kind === 'hint') return { mode: 'hint', label: f.label };
  const apply = !!(opts && opts.apply);
  if (f.kind === 'action') {
    return APPLY_ACTIONS.has(f.id) && apply ? { mode: 'apply', label: f.label, focusId: null } : { mode: 'hint', label: f.label };
  }
  let focusId = null;
  if (f.kind === 'focus' && f.focus !== null) focusId = f.focus;
  if (focusId === null && f.set) {
    const first = Object.keys(f.set).map(Number).find((x) => Number.isFinite(x));
    if (first !== undefined) focusId = first;
  }
  if (focusId === null && Array.isArray(settings) && settings.length) focusId = Number(settings[0]);
  if (f.kind === 'set' && apply) return { mode: 'apply', label: f.label, focusId: Number.isFinite(focusId) ? focusId : null };
  if (focusId === null || !Number.isFinite(focusId)) return { mode: 'hint', label: f.label };
  return { mode: 'link', focusId, label: f.label, sets: f.kind === 'set' };
}

/**
 * A failed fix / dismiss -> what to tell the customer: the server's own message (its words; the doctor's errors carry one), except
 * where that message has internal words (settings_write_off names a feature flag): then a plain sentence of ours. `gone` = the
 * finding is no longer there (404 fix_not_offered / nothing_to_dismiss: reload the panel); `missing` = a server without the route.
 */
export function fixErrorView(err) {
  const res = err && err.response;
  const status = res && typeof res.status === 'number' ? res.status : null;
  const data = res && res.data && typeof res.data === 'object' ? res.data : null;
  const code = data && typeof data.error === 'string' && /^[a-z][a-z0-9_]*$/.test(data.error) ? data.error : null;
  const message = data ? str(data.message) : null;
  if (code === 'settings_write_off') return { code, status, text: 'Saving settings is switched off right now.', gone: false, missing: false };
  if (!code && (status === 404 || status === 405)) return { code: null, status, text: null, gone: false, missing: true };
  return {
    code, status, text: message || 'That could not be saved just now. Try again in a minute.',
    gone: code === 'fix_not_offered' || code === 'nothing_to_dismiss', missing: false,
  };
}

/** The settings page of a slot opened on one setting. */
export function settingsHref(slotName, focusId) {
  const base = `/slots/${encodeURIComponent(slotName)}/settings`;
  return focusId === null || focusId === undefined ? base : `${base}?focus=${encodeURIComponent(focusId)}`;
}

/** The ?focus=<id> of a settings URL (a setting number only), or null. */
export function focusFromSearch(search) {
  const m = /[?&]focus=(\d{1,5})(?:&|$)/.exec(String(search || ''));
  return m ? Number(m[1]) : null;
}

/* ------------------------------------------------------------------ the save */

/**
 * The findings a save shows (design 5.6): every BLOCK, and the WARN / INFO whose input settings this save touched; dismissed ones
 * never. `savedIds` = the ids the page sent. A stage-5 save answer filters itself; the page applies the same rule to the panel's
 * findings when the save answer carries none.
 */
export function saveFindings(findings, savedIds) {
  const ids = new Set((Array.isArray(savedIds) ? savedIds : []).map(Number).filter(Number.isFinite));
  return (Array.isArray(findings) ? findings : []).map((x) => (x && x.text && x.fixes ? x : normFinding(x))).filter(Boolean)
    .filter((f) => !f.dismissed && (f.level === 'BLOCK' || f.settings.some((s) => ids.has(s))))
    .sort(byFindingLevel);
}

/** The save answer's own findings[] (stage 5), or null when it carries none (a stage-3 server). */
export function answerFindings(data) {
  return data && Array.isArray(data.findings) ? data.findings.map(normFinding).filter(Boolean) : null;
}

/**
 * Review SF3: the findings a stage-5 save answer carries are shown AS SENT - the server already kept the ones this save touched
 * (SD02's save line and SD13 come with settings [], so the panel's touched-ids rule would drop them). Dismissed ones never; BLOCK
 * first, the server's order kept within a level.
 */
export function answerSaveFindings(findings) {
  return (Array.isArray(findings) ? findings : []).map((x) => (x && x.text && x.fixes ? x : normFinding(x))).filter(Boolean)
    .filter((f) => !f.dismissed).map((f, i) => ({ f, i })).sort((a, b) => byFindingLevel(a.f, b.f) || a.i - b.i).map((x) => x.f);
}

/**
 * Review MF1: stage 5 puts each finding's text in the save answer's notes[] too (for older pages: DoctorSaveCheck.before), so the
 * notes the save check already says (its toast + the card) are left out of the page's note toasts: one warning per finding, never
 * two. Every other note (SD35 rewrites, SD37, 723's caption note) is kept, in its order.
 */
export function notesNotShown(notes, shownFindings) {
  const said = new Set((Array.isArray(shownFindings) ? shownFindings : []).map((f) => f && str(f.text)).filter(Boolean));
  return (Array.isArray(notes) ? notes : []).filter((t) => !said.has(str(t)));
}

/**
 * SD36: the settings a save asked for and did not write (blocked[]: {id, label, gate, reason}) -> the warning toast, or null when
 * nothing was refused. A save answer that carries its own SD36 finding (stage 5: the server renders the sentence) is shown as it is;
 * otherwise SD36_TOAST is filled with the count and the ids (review SF6: the ids only - r16pd's gate reasons are not catalogue words
 * and may name internal fields). `description` = the first refusal's reason when it is the doctor's own (gate "doctor:SDnn": a
 * catalogue sentence, linted by the server), shown under the sentence, never inside it.
 */
export function blockedToast(blocked, data) {
  const list = (Array.isArray(blocked) ? blocked : []).filter((b) => b && (b.id !== undefined && b.id !== null));
  if (!list.length) return null;
  const ids = list.map((b) => Number(b.id));
  const own = data && Array.isArray(data.findings) ? data.findings.map(normFinding).find((f) => f && f.rule === 'SD36') : null;
  if (own) return { text: own.text, description: null, ids, source: 'server' };
  const doctorReason = list.map((b) => (typeof b.gate === 'string' && /^doctor:SD\d+$/.test(b.gate) ? str(b.reason) : null)).find(Boolean) || null;
  return {
    text: SD36_TOAST.replace('{n}', String(list.length)).replace('{list}', list.map((b) => String(b.id)).join(', ')),
    description: doctorReason, ids, source: 'catalogue copy',
  };
}

/* ------------------------------------------------------------------ Follow-back (SD22) */

/** SD22 findings / holds of several slots' panels -> one note: the server's text and the slots it names, or null. */
export function followBackNote(panels) {
  const slots = [];
  let text = null;
  let fix = null;
  (Array.isArray(panels) ? panels : []).forEach((p) => {
    if (!p || !p.slot) return;
    const hit = [...(p.findings || []), ...(p.banners || []), ...(p.infoHolds || [])].find((x) => x && x.rule === 'SD22');
    if (!hit) return;
    slots.push(p.slot);
    if (!text) text = hit.text;
    if (!fix) fix = (hit.fixes && hit.fixes[0]) || hit.fix || null;
  });
  return slots.length ? { text, slots, fix } : null;
}

/* ------------------------------------------------------------------ time */

const pad = (n) => String(n).padStart(2, '0');
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "Oct 26, 11:40" in the browser's time zone (a hold's since / until arrive in UTC). */
export function fmtWhen(ms) {
  if (!Number.isFinite(ms)) return '';
  const d = new Date(ms);
  return `${MONTHS[d.getMonth()]} ${d.getDate()}, ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** "just now", "5 min ago", "2 h ago", "3 d ago". */
export function fmtAgo(ms, now = Date.now()) {
  if (!Number.isFinite(ms)) return '';
  const m = Math.max(0, Math.floor((now - ms) / 60000));
  if (m < 1) return 'just now';
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h} h ago`;
  return `${Math.floor(h / 24)} d ago`;
}

/** A count line for the console: rule ids only, never a sentence (deep logging, no message text). */
export function rulesLine(list) {
  const ids = (Array.isArray(list) ? list : []).map((x) => x && (x.rule || x.key)).filter(Boolean);
  return ids.length ? ids.join(',') : 'none';
}
